import { Box, Button, Flex, Stack, Text, Image, Tabs } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchAPI } from '@/api/fetch';
import { apiGetHome, GetHomeResponse } from '@/api/services';
import useAuth from '@/store/auth';
import { CandidateWithJobContextBasicInfo } from '@/types/Candidate';
import { JobBasicInfo } from '@/types/Job';
import {
  getJobCandidateStatusLabel,
  getEmploymentTypeLabel,
  CandidateSource,
  JobCandidateStatus
} from '@/types/enums';
import emptyJobImage from '@/assets/image/empty-job-cover.svg';
import emptyActionItemImage from '@/assets/image/empty-action-item-cover.svg';
import { Plus } from '@phosphor-icons/react';
import { WelcomeDialog } from './WelcomeDialog';
import { Screening } from '@/types/Screening';
import { MeetingSummary } from '@/types/Meeting';
import ScreeningCard from '@/views/Interview/Agent/ScreeningCard';
import RecentInterviewCard from '@/views/Interview/Copilot/RecentInterviewCard';
import { LoadingSkeleton } from '@/components/layout/LoadingSkeleton';
import { getCandidateFullName } from '@/utils';

export default function Home() {
  const auth = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [jobs, setJobs] = useState<JobBasicInfo[]>([]);
  const [actionItems, setActionItems] = useState<CandidateWithJobContextBasicInfo[]>([]);
  const [screenings, setScreenings] = useState<Screening[]>([]);
  const [meetings, setMeetings] = useState<MeetingSummary[]>([]);
  const [actionItemCount, setActionItemCount] = useState(0);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('screenings');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchHomeData = async () => {
      setIsLoading(true);
      try {
        const response = await fetchAPI<GetHomeResponse>({
          request: apiGetHome
        });
        if (response.success) {
          const data = response.data;
          setJobs(data.jobs);
          setActionItems(data.action_items);
          setScreenings(data.recent_screenings);
          setMeetings(data.recent_meetings);
          setActionItemCount(data.action_item_count);

          if (response.data.jobs.length === 0) {
            const hasSeenWelcomeDialog = localStorage.getItem('hasSeenWelcomeDialog');
            if (!hasSeenWelcomeDialog) {
              setShowWelcomeDialog(true);
            }
          }
        } else {
          console.error('Failed to fetch home data:', response.message);
        }
      } catch (error) {
        console.error('Error fetching home data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  const getCreatedDateFromNow = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 0 ? 'Today' : `${diffDays} days ago`;
  };

  const handleGetStarted = () => {
    setShowWelcomeDialog(false);
    localStorage.setItem('hasSeenWelcomeDialog', 'true');
  };

  const handleCloseWelcomeDialog = () => {
    setShowWelcomeDialog(false);
    localStorage.setItem('hasSeenWelcomeDialog', 'true');
  };

  const updateScreening = (updatedScreening: Screening, removed: boolean) => {
    if (removed) {
      setScreenings((prev) => prev.filter((s) => s.screening_id !== updatedScreening.screening_id));
    } else {
      setScreenings((prev) =>
        prev.map((s) => (s.screening_id === updatedScreening.screening_id ? updatedScreening : s))
      );
    }
  };

  return (
    <Flex w="100%" flexDir="column" p="20px 32px">
      <Flex justify="flex-start" align="center" mb="16px">
        <Text fontSize="28px" color="neutrals.7900">
          Welcome, {auth.account?.name} 👋🏻
        </Text>
      </Flex>
      <Stack direction="row" justify="flex-start" align="flex-start" gap="24px" h="100%">
        <Flex flex={2} direction="column" h="calc(100vh - 100px)" w="100%" gap="16px">
          <Box
            flex="1"
            w="100%"
            p="16px 32px"
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="12px"
            boxShadow="sm"
            _hover={{ boxShadow: 'md' }}
            transition="all 0.2s ease-in-out"
            minH={0}
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" w="100%">
              <Text fontSize="28px" fontWeight="bold" mb={jobs.length > 0 ? '12px' : '0'}>
                Listed Jobs
              </Text>
              {jobs.length > 0 && (
                <Button
                  variant="ghost"
                  color="neutrals.700"
                  fontWeight="semibold"
                  fontSize="14px"
                  onClick={() => navigate('/job')}
                  _hover={{ bg: 'transparent' }}
                >
                  See all
                </Button>
              )}
            </Flex>
            <Stack gap="16px" w="100%" overflowY="auto" h="calc(100% - 56px)">
              {isLoading ? (
                <LoadingSkeleton count={2} />
              ) : jobs.length > 0 ? (
                jobs.map((job) => (
                  <Box
                    key={job.job_id}
                    w="100%"
                    bg="background.primary"
                    border="1px solid"
                    borderColor="neutrals.200"
                    borderRadius="12px"
                    p="16px 24px"
                    _hover={{ cursor: 'pointer', boxShadow: 'sm' }}
                    transition="all 0.2s ease-in-out"
                    onClick={() => navigate(`/job/details/${job.job_id}`)}
                  >
                    <Stack gap="8px">
                      <Text fontSize="18px" fontWeight="bold" color="neutrals.900">
                        {job.client.name}
                      </Text>
                      <Text fontSize="14px" color="neutrals.800">
                        {job.job_title}
                      </Text>
                      <Flex justify="space-between" fontSize="12px" color="neutrals.500">
                        <Flex>
                          <Text>{getEmploymentTypeLabel(job.employment_type)}</Text>
                          <Text mx="8px"> • </Text>
                          <Text>{job.location}</Text>
                        </Flex>
                        <Flex>
                          <Text>{getCreatedDateFromNow(job.created_at)}</Text>
                        </Flex>
                      </Flex>
                    </Stack>
                  </Box>
                ))
              ) : (
                <Stack w="100%" h="100%" align="center" gap="4px">
                  <Image src={emptyJobImage} h="150px" w="150px" />
                  <Text fontSize="18px" color="neutrals.800" fontWeight="bold">
                    No job posted
                  </Text>
                  <Text fontSize="16px" color="neutrals.500" mb="0px">
                    Let's get started with your first job posting.
                  </Text>
                  <Button
                    border="1px solid"
                    borderRadius="8px"
                    bg="primary.900"
                    color="neutrals.0"
                    p="20px 48px"
                    fontSize="16px"
                    onClick={() => navigate('/job/create')}
                  >
                    <Plus size={24} />
                    Add New Job
                  </Button>
                </Stack>
              )}
            </Stack>
          </Box>
          <Box
            flex="1"
            w="100%"
            p="16px 32px"
            bg="background.secondary"
            border="1px solid"
            borderColor="neutrals.200"
            borderRadius="12px"
            boxShadow="sm"
            _hover={{ boxShadow: 'md' }}
            transition="all 0.2s ease-in-out"
            minH={0}
            display="flex"
            flexDirection="column"
          >
            <Flex justify="space-between" align="center" w="100%">
              <Text fontSize="28px" fontWeight="bold">
                Interviews
              </Text>
              {(screenings.length > 0 || meetings.length > 0) && (
                <Button
                  variant="ghost"
                  color="neutrals.700"
                  fontWeight="semibold"
                  fontSize="14px"
                  onClick={() => navigate('/interview')}
                  _hover={{ bg: 'transparent' }}
                >
                  See all
                </Button>
              )}
            </Flex>

            {isLoading ? (
              <LoadingSkeleton count={2} />
            ) : (
              <Tabs.Root
                variant="enclosed"
                value={activeTab}
                onValueChange={(details) => setActiveTab(details.value)}
                flex="1"
                display="flex"
                flexDirection="column"
                minH={0}
              >
                <Tabs.List w="fit-content" bg="transparent">
                  <Tabs.Trigger
                    value="screenings"
                    fontSize="16px"
                    p="0 16px"
                    color="neutrals.700"
                    borderRadius="12px"
                    _selected={{
                      color: 'primary.900',
                      border: '1px solid',
                      borderColor: 'neutrals.500',
                      fontWeight: 'semibold'
                    }}
                  >
                    Automated
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value="interviews"
                    fontSize="16px"
                    fontWeight="medium"
                    p="0 16px"
                    color="neutrals.700"
                    borderRadius="12px"
                    _selected={{
                      color: 'primary.900',
                      border: '1px solid',
                      borderColor: 'neutrals.500',
                      fontWeight: 'semibold'
                    }}
                  >
                    Copilot
                  </Tabs.Trigger>
                </Tabs.List>

                <Tabs.Content value="screenings" flex="1" minH={0}>
                  <Stack gap="16px" w="100%" overflowY="auto" h="100%">
                    {screenings.length > 0 ? (
                      screenings.map((screening) => (
                        <ScreeningCard
                          key={screening.screening_id}
                          screening={screening}
                          onUpdateScreening={updateScreening}
                        />
                      ))
                    ) : (
                      <Stack w="100%" h="100%" align="center" gap="4px">
                        <Image src={emptyJobImage} h="150px" w="150px" />

                        <Text fontSize="16px" color="neutrals.700" mb="0px">
                          No screenings available
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                </Tabs.Content>

                <Tabs.Content value="interviews" flex="1" minH={0}>
                  <Stack gap="16px" w="100%" overflowY="auto" h="100%">
                    {meetings.length > 0 ? (
                      meetings.map((meeting) => (
                        <RecentInterviewCard
                          key={meeting.meeting_id}
                          meeting={meeting}
                          onClick={() => navigate(`/interview/${meeting.meeting_id}`)}
                        />
                      ))
                    ) : (
                      <Stack w="100%" h="100%" align="center" justify="center">
                        <Text fontSize="18px" color="neutrals.500">
                          No interviews available
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                </Tabs.Content>
              </Tabs.Root>
            )}
          </Box>
        </Flex>
        <Box
          flex={1}
          h="calc(100vh - 100px)"
          w="100%"
          p="16px 32px"
          bg="background.secondary"
          border="1px solid"
          borderColor="neutrals.200"
          borderRadius="12px"
          boxShadow="sm"
          _hover={{ boxShadow: 'md' }}
          transition="all 0.2s ease-in-out"
        >
          <Flex direction="column" justify="flex-start" align="flex-start" h="100%">
            <Text fontSize="24px" fontWeight="bold" mb="20px">
              Pending Actions {actionItemCount > 0 && `(${actionItemCount})`}
            </Text>
            <Stack gap="16px" w="100%" overflowY="auto" h="calc(100% - 56px)">
              {isLoading ? (
                <LoadingSkeleton count={4} />
              ) : actionItems.length > 0 ? (
                actionItems.map((item) => {
                  const isNew =
                    item.source === CandidateSource.CandidateApplication &&
                    item.status === JobCandidateStatus.ResumeAnalyzed;

                  return (
                    <Box
                      key={item.candidate_id}
                      w="100%"
                      bg={isNew ? 'warning.100' : 'background.primary'}
                      border="1px solid"
                      borderColor="neutrals.200"
                      borderRadius="12px"
                      p="24px"
                      alignItems="center"
                      transition="all 0.2s ease-in-out"
                      _hover={{ boxShadow: 'sm', cursor: 'pointer' }}
                      position="relative"
                    >
                      {isNew && (
                        <Box
                          position="absolute"
                          top={0}
                          right={0}
                          bg="warning.500"
                          color="neutrals.0"
                          fontSize="xs"
                          fontWeight="bold"
                          px={3}
                          py={1}
                          borderTopRightRadius="12px"
                          borderBottomLeftRadius="12px"
                          zIndex={1}
                        >
                          NEW
                        </Box>
                      )}
                      <Flex justify="space-between" align="center">
                        <Stack gap="8px" alignContent="center">
                          <Text fontSize="14px" fontWeight="semibold" color="neutrals.700">
                            {item.job.client.name} - {item.job.job_title}
                          </Text>
                          <Text fontSize="16px" color="neutrals.900" fontWeight="bold">
                            {getCandidateFullName(item.first_name, item.last_name)}
                          </Text>
                          <Text fontSize="14px" color="neutrals.700">
                            {getJobCandidateStatusLabel(item.status)}
                          </Text>
                        </Stack>
                        <Button
                          variant="outline"
                          color="primary.900"
                          bg="background.primary"
                          borderColor="primary.900"
                          borderRadius="8px"
                          fontSize="14px"
                          px="16px"
                          py="8px"
                          onClick={() =>
                            navigate(
                              `/job/details/${item.job.job_id}/candidate/${item.candidate_id}`
                            )
                          }
                          _hover={{ bg: 'primary.900', color: 'neutrals.0' }}
                        >
                          View
                        </Button>
                      </Flex>
                    </Box>
                  );
                })
              ) : (
                <Stack w="100%" h="100%" align="center">
                  <Image src={emptyActionItemImage} h="200px" w="200px" mt="120px" mb="12px" />
                  <Text fontSize="18px" color="neutrals.800" mb="16px">
                    No action items
                  </Text>
                </Stack>
              )}
            </Stack>
          </Flex>
        </Box>
      </Stack>
      <WelcomeDialog
        isOpen={showWelcomeDialog}
        onClose={handleCloseWelcomeDialog}
        onGetStarted={handleGetStarted}
      />
    </Flex>
  );
}
